import { Component, OnInit, AfterViewChecked, QueryList, ViewChildren, ElementRef, HostListener, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgStyle } from '@angular/common';
import { BaseQuestionComponent } from '../base-question.component';
import { QuestionOption } from '@formrails/form-builder/data-access';

/**
 * Dropdown question component.
 * Displays and configures a dropdown/select list question.
 * Uses Angular signals inherited from BaseQuestionComponent.
 */
@Component({
  selector: 'app-dropdown-question',
  standalone: true,
  imports: [FormsModule, NgStyle],
  templateUrl: './dropdown-question.component.html',
  styleUrl: './dropdown-question.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DropdownQuestionComponent extends BaseQuestionComponent implements OnInit, AfterViewChecked {
  @ViewChildren('optionInput') optionInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('dropdownContainer', { static: false }) dropdownContainer!: ElementRef;
  @ViewChild('dropdownTrigger', { static: false }) dropdownTrigger!: ElementRef;

  private cdr = inject(ChangeDetectorRef);

  options: QuestionOption[] = [];
  selectedOptionIndex: number | null = null;
  private newOptionIndex: number | null = null;

  // Searchable dropdown state
  searchTerm: string = '';
  isDropdownOpen: boolean = false;
  filteredOptions: (string | QuestionOption)[] = [];

  // Dropdown menu position (for fixed positioning)
  dropdownMenuStyle: { [key: string]: string } = {};

  ngOnInit(): void {
    const q = this.question();
    // Initialize options from question model or start with one empty option
    if (q.options && q.options.length > 0) {
      // Convert string[] to QuestionOption[] if needed (backward compatibility)
      if (typeof q.options[0] === 'string') {
        this.options = (q.options as string[]).map(text => ({
          text,
          isCustomOption: false
        }));
      } else {
        this.options = (q.options as QuestionOption[]).map(opt => ({
          text: opt.text,
          isCustomOption: opt.isCustomOption || false,
          customValue: opt.customValue
        }));
      }
    } else {
      this.options = [{ text: '', isCustomOption: false }];
    }

    // Initialize custom option values from question model
    // Create a new object if customOptionValues doesn't exist (question may be frozen)
    if (!q.customOptionValues) {
      this.emitQuestionChange({ ...q, customOptionValues: {} });
    }

    // Initialize filtered options for searchable dropdown
    this.updateFilteredOptions();
  }

  onLabelChange(value: string): void {
    this.updateLabel(value);
  }

  onHelperTextChange(value: string): void {
    this.updateHelperText(value);
  }

  onAdditionalInfoChange(value: string): void {
    this.updateAdditionalInfo(value);
  }

  onSearchableToggle(enabled: boolean): void {
    this.emitQuestionChange({ ...this.question(), isSearchable: enabled });
  }

  onDynamicOptionsToggle(enabled: boolean): void {
    this.emitQuestionChange({ ...this.question(), hasDynamicOptions: enabled });
  }

  // Dynamic options configuration methods
  onSourceContainerChange(value: string): void {
    this.emitQuestionChange({ ...this.question(), sourceContainer: value });
  }

  onTextPropertyChange(value: string): void {
    this.emitQuestionChange({ ...this.question(), textProperty: value });
  }

  onCustomOptionIdChange(value: string): void {
    this.emitQuestionChange({ ...this.question(), customOptionId: value });
  }

  onCustomOptionTextChange(value: string): void {
    this.emitQuestionChange({ ...this.question(), customOptionText: value });
  }

  onOptionChange(index: number, value: string): void {
    // Update local array and persist immediately so typed text isn't lost if
    // the user clicks Accept without blurring the input.
    this.options[index] = { ...this.options[index], text: value };
    this.saveOptions();
  }

  onOptionBlur(): void {
    // Kept for symmetry with radio-question; ensures a save happens even if a
    // downstream consumer suppressed the input events.
    this.saveOptions();
  }

  onCustomOptionToggle(index: number, enabled: boolean): void {
    this.options[index] = { ...this.options[index], isCustomOption: enabled };
    this.saveOptions();
  }

  onCustomValueChange(index: number, value: string): void {
    this.options[index] = { ...this.options[index], customValue: value };
    const q = this.question();
    // Create a new customOptionValues object (question may be frozen)
    const currentCustomValues = q.customOptionValues || {};
    const updatedCustomValues = { ...currentCustomValues, [index]: value };
    this.emitQuestionChange({ ...q, customOptionValues: updatedCustomValues });
  }

  trackByIndex(index: number): number {
    return index;
  }

  addOption(): void {
    const newIndex = this.options.length;
    this.options.push({ text: '', isCustomOption: false });
    this.newOptionIndex = newIndex;
    this.saveOptions();
  }

  ngAfterViewChecked(): void {
    if (this.newOptionIndex !== null && this.optionInputs && this.optionInputs.length > this.newOptionIndex) {
      const inputElement = this.optionInputs.toArray()[this.newOptionIndex]?.nativeElement;
      if (inputElement) {
        setTimeout(() => {
          inputElement.focus();
          this.newOptionIndex = null;
        }, 0);
      }
    }
  }

  removeOption(index: number): void {
    // Keep at least one option so the picker doesn't collapse; matches
    // radio-question / checkbox-question behaviour.
    if (this.options.length <= 1) return;
    this.options.splice(index, 1);
    const q = this.question();
    // Update custom option values indices
    // Create a new object (question may be frozen)
    if (q.customOptionValues) {
      const newCustomValues: Record<number, string> = {};
      Object.keys(q.customOptionValues).forEach(key => {
        const keyNum = parseInt(key, 10);
        if (keyNum < index) {
          newCustomValues[keyNum] = q.customOptionValues![keyNum];
        } else if (keyNum > index) {
          newCustomValues[keyNum - 1] = q.customOptionValues![keyNum];
        }
      });
      this.emitQuestionChange({ ...q, customOptionValues: newCustomValues });
    }

    // If all options were removed, add an empty placeholder option
    if (this.options.length === 0) {
      this.options.push({ text: '', isCustomOption: false });
    }

    this.saveOptions();
  }

  onSelectChange(value: string): void {
    const index = this.options.findIndex(opt => opt.text === value);
    this.selectedOptionIndex = index >= 0 ? index : null;
  }

  isOptionSelected(index: number): boolean {
    return this.selectedOptionIndex === index;
  }

  getOptionsForDisplay(): (string | QuestionOption)[] {
    const q = this.question();
    if (!q.options || q.options.length === 0) {
      return [];
    }
    return q.options;
  }

  /**
   * Check if the dropdown has populated options (used in view mode)
   * Returns true if there are options with actual text content
   */
  hasPopulatedOptions(): boolean {
    const q = this.question();
    if (!q.options || q.options.length === 0) {
      return false;
    }
    // Check if at least one option has text content
    return q.options.some(opt => {
      const text = typeof opt === 'string' ? opt : opt.text;
      return text && text.trim().length > 0;
    });
  }

  getOptionText(option: string | QuestionOption): string {
    if (typeof option === 'string') {
      return option;
    }
    return option.text;
  }

  // Searchable dropdown methods
  onSearchInputChange(value: string): void {
    this.searchTerm = value;
    this.updateFilteredOptions();
  }

  updateFilteredOptions(): void {
    const allOptions = this.getOptionsForDisplay();
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredOptions = allOptions;
    } else {
      const searchLower = this.searchTerm.toLowerCase();
      this.filteredOptions = allOptions.filter(opt => {
        const text = this.getOptionText(opt).toLowerCase();
        return text.includes(searchLower);
      });
    }
  }

  toggleDropdown(): void {
    // Allow opening dropdown even in disabled/read-only mode to view options
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.searchTerm = '';
      this.updateFilteredOptions();
      this.calculateDropdownPosition();
    }
  }

  private calculateDropdownPosition(): void {
    if (!this.dropdownTrigger?.nativeElement) return;

    const triggerRect = this.dropdownTrigger.nativeElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const dropdownMaxHeight = 300; // matches CSS max-height

    // Determine if dropdown should open above or below
    const openAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    if (openAbove) {
      this.dropdownMenuStyle = {
        'position': 'fixed',
        'bottom': `${viewportHeight - triggerRect.top + 4}px`,
        'left': `${triggerRect.left}px`,
        'width': `${triggerRect.width}px`,
        'top': 'auto'
      };
    } else {
      this.dropdownMenuStyle = {
        'position': 'fixed',
        'top': `${triggerRect.bottom + 4}px`,
        'left': `${triggerRect.left}px`,
        'width': `${triggerRect.width}px`,
        'bottom': 'auto'
      };
    }

    this.cdr.markForCheck();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onWindowChange(): void {
    if (this.isDropdownOpen) {
      this.calculateDropdownPosition();
    }
  }

  selectOption(option: string | QuestionOption): void {
    const optionText = this.getOptionText(option);
    const index = this.options.findIndex(opt => opt.text === optionText);
    this.selectedOptionIndex = index >= 0 ? index : null;
    this.isDropdownOpen = false;
    this.searchTerm = '';
    this.updateFilteredOptions();
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.searchTerm = '';
    this.updateFilteredOptions();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isDropdownOpen) return;

    const target = event.target as HTMLElement;
    if (this.dropdownContainer?.nativeElement && !this.dropdownContainer.nativeElement.contains(target)) {
      this.closeDropdown();
    }
  }

  private saveOptions(): void {
    // Preserve the full options list (including empty rows) so newly-added or
    // partially-typed entries survive between edits. Empty entries are only
    // pruned at final-form-submit time by the consumer.
    const optionsToSave = this.options.length > 0
      ? [...this.options]
      : [{ text: '', isCustomOption: false }];
    this.emitConfigChange({ options: optionsToSave } as any);
    this.emitQuestionChange({ ...this.question(), options: optionsToSave });
  }

  onRequiredToggle(): void {
    this.updateRequired(!this.question().isRequired);
  }
}

